import { getResource } from '@/shared/constants/resources'
import { api } from '@/shared/lib/api'

import { isBonusResolution } from './navigateFromQrResolution'

export type QrResolution =
  | { status: 'resolved'; resourceType: string; resource: Record<string, unknown>; path: string }
  | { status: 'link'; destination: string; external: boolean }
  | { status: 'unavailable'; resourceType: string; resource: Record<string, unknown>; path: string }
  | { status: 'not_found' }
  | { status: 'technical_error'; error: unknown }

// `RESOURCES` (shared/constants/resources.ts) não tem uma interface comum —
// cada entrada declara só os campos que usa (`getResourcePath` e/ou
// `basePath`, ambos opcionais dependendo do recurso). `getResource` retorna
// a união de todas as entradas; sem este shape local, o acesso a
// `.getResourcePath`/`.basePath` falha em compile-time para os membros da
// união que não declaram o campo.
type ResourceConfig = {
  basePath?: string
  getResourcePath?: (args: { resource: Record<string, unknown> }) => string | undefined
}

// Ponto único de resolução de leitura de QR code: recebe o `code` (já
// extraído pelo consumidor, seja da query `?qr_code=` ou de uma URL
// escaneada) e devolve um contrato fechado — nunca lança. Duas camadas de
// falha, cada uma com seu catch: (1) a chamada de rede — 404 (code
// inexistente) vira `not_found`, qualquer outra falha (rede fora do ar,
// timeout, 5xx, 401 de sessão expirada) vira `technical_error`, nunca a
// mesma mensagem de "QR não existe"; (2) a resolução do recurso já
// respondido pela API (tipo desconhecido, sem uid/id, sem config de path)
// é sempre de domínio, nunca falha técnica, e vira `not_found`.
export const resolveQrCode = async (code: string): Promise<QrResolution> => {
  let data: {
    record: {
      resource_type: string
      resource: Record<string, unknown>
      kind?: string
      destination?: string | null
      external_destination?: boolean | null
    }
  }

  try {
    ;({ data } = await api.get(`/user/qr_codes/${code}`))
  } catch (error) {
    const httpStatus = (error as { response?: { status?: number } })?.response?.status
    if (httpStatus === 404) return { status: 'not_found' }

    return { status: 'technical_error', error }
  }

  // Erros daqui pra baixo são de domínio (tipo de recurso desconhecido,
  // recurso sem uid/id, config de path ausente) — nunca falha técnica, então
  // ficam num catch à parte, sempre `not_found`.
  try {
    // QR code de link não tem recurso para resolver: o destino é dele. Sem
    // destino não há para onde ir — falha de domínio, o mesmo desfecho de um
    // código inexistente.
    //
    // `external` sem classificação vira `true`: pedir confirmação a mais é o
    // erro barato; levar o participante para fora sem avisar é o caro.
    if (data.record.kind === 'link') {
      const destination = data.record.destination

      if (!destination) return { status: 'not_found' }

      return {
        status: 'link',
        destination,
        external: data.record.external_destination !== false
      }
    }

    const { resource_type: resourceType, resource } = data.record

    const resourceConfig = getResource(resourceType) as ResourceConfig
    const resourceUid = resource?.uid ?? resource?.id

    if (!resourceUid) return { status: 'not_found' }

    const path = resourceConfig.getResourcePath
      ? resourceConfig.getResourcePath({ resource })
      : `${resourceConfig.basePath}/${resourceUid}`

    // `getResourcePath` pode devolver undefined quando o recurso existe mas
    // não carrega o campo que o path depende (ex.: GamificationPage sem
    // slug — a home reservada da gamificação, que só é alcançada por `/`).
    // Sem path não há pra onde navegar: mesmo resultado de "não encontrado".
    if (!path) return { status: 'not_found' }

    const bonusAlreadyRedeemed =
      isBonusResolution(resourceType) &&
      resource?.redeemable === false &&
      resource?.unavailable_reason === 'limit_reached'

    // `checkin_active` só existe hoje no payload de Partner/Programming
    // (mesma migração de partner-checkin-active-window); qualquer outro
    // resource_type simplesmente não traz o campo, então `=== false` nunca
    // bate e o recurso segue para `resolved` por omissão.
    if (
      resource?.checkin_active === false ||
      (resource?.redeemable === false && !bonusAlreadyRedeemed)
    ) {
      return { status: 'unavailable', resourceType, resource, path }
    }

    return { status: 'resolved', resourceType, resource, path }
  } catch {
    return { status: 'not_found' }
  }
}
